import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Hash, AlertCircle, CheckCircle } from 'lucide-react';
import { PListLineDto, OrderSerialNoDto } from '../../../../../types/picking';
import { pickingService } from '../../../../../services/pickingService';

interface ExtendedPickingLine extends PListLineDto {
  itemDescription?: string;
}

interface SerialNumberModalProps {
  isOpen: boolean;
  line: ExtendedPickingLine | null;
  pListId: number;
  onClose: () => void;
  onSerialNumbersChanged: () => void;
}

export const SerialNumberModal: React.FC<SerialNumberModalProps> = ({
  isOpen,
  line,
  pListId,
  onClose,
  onSerialNumbersChanged
}) => {
  const [serialNumbers, setSerialNumbers] = useState<OrderSerialNoDto[]>([]);
  const [newSerialNo, setNewSerialNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load serial numbers when modal opens
  useEffect(() => {
    if (isOpen && line) {
      loadSerialNumbers();
      setNewSerialNo('');
      setError(null);
    }
  }, [isOpen, line]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const loadSerialNumbers = async () => {
    if (!line) return;

    setLoading(true);
    try {
      const data = await pickingService.getSerialNumbers(pListId, line.orderNo, line.lineNo);
      setSerialNumbers(data);
    } catch (err) {
      console.error('Error loading serial numbers:', err);
      // Use the ones from the line if available
      setSerialNumbers(line.serialNumbers || []);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSerialNumber = async () => {
    if (!line || !newSerialNo.trim()) return;

    // Check if serial number already exists
    if (serialNumbers.some(s => s.serialNo.toLowerCase() === newSerialNo.trim().toLowerCase())) {
      setError('Dette serienummeret er allerede registrert');
      return;
    }

    // Check if we've reached the quantity limit
    if (serialNumbers.length >= line.qty) {
      setError(`Kan ikke registrere flere enn ${line.qty} serienummer (bestilt antall)`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const created = await pickingService.createSerialNumber(
        pListId,
        line.orderNo,
        line.lineNo,
        newSerialNo.trim()
      );
      setSerialNumbers([...serialNumbers, created]);
      setNewSerialNo('');
      onSerialNumbersChanged();
      inputRef.current?.focus();
    } catch (err: any) {
      setError(err.message || 'Kunne ikke lagre serienummer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSerialNumber = async (serialNoId: number) => {
    setSaving(true);
    setError(null);

    try {
      await pickingService.deleteSerialNumber(pListId, serialNoId);
      setSerialNumbers(serialNumbers.filter(s => s.id !== serialNoId));
      onSerialNumbersChanged();
    } catch (err: any) {
      setError(err.message || 'Kunne ikke slette serienummer');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newSerialNo.trim()) {
      handleAddSerialNumber();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen || !line) return null;

  const registeredCount = serialNumbers.length;
  const requiredCount = Math.floor(line.qty);
  const isComplete = registeredCount >= requiredCount;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Hash className="text-blue-600" size={24} />
            <h3 className="text-lg font-semibold">Registrer serienummer</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item info */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">Varenr:</span>
              <span className="ml-2 font-medium">{line.itemNo}</span>
            </div>
            <div>
              <span className="text-gray-500">Antall:</span>
              <span className="ml-2 font-medium">{line.qty}</span>
            </div>
          </div>
          {line.itemDescription && (
            <div className="mt-1 text-sm text-gray-600 truncate">
              {line.itemDescription}
            </div>
          )}
        </div>

        {/* Progress indicator */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              Registrert: {registeredCount} / {requiredCount}
            </span>
            {isComplete ? (
              <span className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle size={16} />
                Komplett
              </span>
            ) : (
              <span className="text-orange-600 text-sm">
                Gjenstår: {requiredCount - registeredCount}
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.min((registeredCount / requiredCount) * 100, 100)}%` }}
            />
          </div>
        </div>

        {/* Input area */}
        <div className="p-4 border-b">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={newSerialNo}
              onChange={(e) => setNewSerialNo(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Skann eller skriv serienummer..."
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={saving || isComplete}
              autoComplete="off"
            />
            <button
              onClick={handleAddSerialNumber}
              disabled={!newSerialNo.trim() || saving || isComplete}
              className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Plus size={20} />
              <span className="hidden sm:inline">Legg til</span>
            </button>
          </div>

          {error && (
            <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        {/* Serial numbers list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center text-gray-500 py-8">
              Laster serienummer...
            </div>
          ) : serialNumbers.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              Ingen serienummer registrert enn\u00e5
            </div>
          ) : (
            <div className="space-y-2">
              {serialNumbers.map((serial, index) => (
                <div
                  key={serial.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 text-sm font-medium w-6">
                      {index + 1}.
                    </span>
                    <span className="font-mono font-medium">{serial.serialNo}</span>
                  </div>
                  <button
                    onClick={() => handleDeleteSerialNumber(serial.id)}
                    disabled={saving}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    title="Slett serienummer"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
};
